// Pure helpers for CallDetailPage and its sub-components.

// Capitalize call type properly: "it_support" → "IT Support", "b2b_sales" → "B2B Sales"
export const formatCallType = (callType) => {
  if (!callType) return '';
  return callType
    .split('_')
    .map(word => {
      const upper = word.toUpperCase();
      if (['IT', 'MSP', 'BDM', 'B2B', 'HR', 'CRM'].includes(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Strip backslash-escaped characters from transcriptions stored with Python escaping
export const cleanTranscript = (text) => {
  if (!text) return '';
  return text.replace(/\\'/g, "'").replace(/\\"/g, '"').replace(/\\\\/g, '\\');
};

// Full Australian phone normalization
export const normalizeAuPhone = (raw) => {
  if (!raw) return null;
  let n = raw;
  try { n = decodeURIComponent(n); } catch (e) { console.debug('normalizeAuPhone: input was not URI-encoded, using raw', e?.message); /* keep raw */ }
  if (n.includes('@')) n = n.split('@')[0];
  if (!n) return null;
  if (/^\d{1,6}$/.test(n)) return n;
  if (n.startsWith('+61') && n.length >= 12) return '0' + n.slice(3);
  if (/^61\d{9}$/.test(n)) return '0' + n.slice(2);
  if (/^[23478]\d{8}$/.test(n)) return '0' + n;
  return n;
};

export const formatPhoneNumber = normalizeAuPhone;

// Only show customer field if value is meaningful (not empty, null, or "0")
export const isMeaningfulValue = (val) => val && val !== '0' && val.trim() !== '';

// Parse a "speaker: text" transcript into structured lines with an
// "isAgent" heuristic so the UI can colour agent vs customer differently.
//
// Heuristics (in order):
//   1. Speaker label EXACTLY matches call.agent_id           → agent
//   2. Speaker label matches call.agent_name or domain name   → agent
//   3. Speaker label matches call.customer_phone (or the raw  → customer
//      "61XXXXXXXXX" form of it)
//   4. Fallback: first distinct speaker = customer, second = agent
//      (Yabbit transcripts almost always list the caller first.)
//
// Returns [{ speaker, text, isAgent }, ...]. Lines that don't match the
// "speaker: text" pattern are grouped under the previous speaker so
// mid-utterance wraps don't cause them to disappear.
export const parseTranscriptLines = (raw, { agentId, agentName, domainName, customerPhone } = {}) => {
  if (!raw) return [];
  const lines = raw.split(/\n/).map((l) => l.trim()).filter(Boolean);
  const norm = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '');

  const agentTokens = [agentId, agentName, domainName].filter(Boolean).map(norm);
  const customerTokens = [
    customerPhone,
    (customerPhone || '').replace(/^0/, '61'),
    (customerPhone || '').replace(/^\+?61/, '0'),
  ].filter(Boolean).map(norm);

  const speakerOrder = [];
  const speakerIsAgent = {};

  const parsed = lines.map((line) => {
    const m = /^([^:]{1,50}?):\s+(.*)$/.exec(line);
    if (!m) return { speaker: null, text: line, isAgent: null, unlabelled: true };
    const speaker = m[1].trim();
    const text = m[2].trim();
    const nspk = norm(speaker);
    if (!speakerOrder.includes(nspk)) speakerOrder.push(nspk);
    if (!(nspk in speakerIsAgent)) {
      if (agentTokens.some((t) => nspk.includes(t) || t.includes(nspk))) speakerIsAgent[nspk] = true;
      else if (customerTokens.some((t) => nspk === t)) speakerIsAgent[nspk] = false;
    }
    return { speaker, text, isAgent: null, _key: nspk };
  });

  // Fallback: infer via first-appearance order.
  if (speakerOrder.length && !speakerOrder.every((s) => s in speakerIsAgent)) {
    // The FIRST unique speaker is usually the caller (Yabbit puts caller
    // on the inbound leg first). If tokens pointed us the other way, we
    // respect that; otherwise assume [caller, agent, ...].
    speakerOrder.forEach((s, i) => {
      if (!(s in speakerIsAgent)) speakerIsAgent[s] = i !== 0;
    });
  }

  // Propagate isAgent + carry unlabelled lines under the previous speaker
  let last = null;
  return parsed.map((row) => {
    if (row.unlabelled && last) return { ...row, speaker: last.speaker, isAgent: last.isAgent, unlabelled: false };
    if (row._key) row.isAgent = speakerIsAgent[row._key] ?? false;
    last = row;
    return row;
  });
};

export const getScoreColor = (score) => {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-rose-600';
};

export const getStatusBadgeClass = (status) => {
  if (status === 'escalated') return 'badge badge-error';
  if (status === 'analyzed') return 'badge badge-success';
  return 'badge badge-neutral';
};
